import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useSnackbar } from 'notistack'
import { invoicesAPI, formatApiError } from '../utils/api'

export const useInvoices = (params = {}) => {
  return useQuery(
    ['invoices', params],
    () => invoicesAPI.getAll(params).then(res => res.data),
    {
      keepPreviousData: true,
      staleTime: 30 * 1000, // 30 seconds
      refetchOnWindowFocus: true, // Refetch when window gains focus
      refetchOnMount: true, // Always refetch on mount
    }
  )
}

export const useInvoice = (id) => {
  return useQuery(
    ['invoice', id],
    () => {
      console.log('🔍 DEBUG - useInvoice - fetching invoice with id:', id)
      return invoicesAPI.getById(id).then(res => {
        console.log('🔍 DEBUG - useInvoice - API response:', res.data)
        return res.data
      })
    },
    {
      enabled: !!id,
      refetchOnWindowFocus: true, // Refetch when window gains focus
      refetchOnMount: true, // Always refetch on mount
    }
  )
}

export const useCreateInvoice = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    (formData) => invoicesAPI.create(formData),
    {
      onSuccess: () => {
        // Invalidate all related queries
        queryClient.invalidateQueries(['invoices'])
        queryClient.invalidateQueries(['dashboard'])
        queryClient.invalidateQueries(['clients'])
        
        // Force immediate refetch of dashboard
        queryClient.refetchQueries(['dashboard'], { active: true })
        
        enqueueSnackbar('Factura creada exitosamente', { variant: 'success' })
      },
      onError: (error) => {
        const message = formatApiError(error)
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

export const useUpdateInvoice = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    ({ id, formData }) => {
      console.log('🔍 DEBUG - useUpdateInvoice - id:', id)
      console.log('🔍 DEBUG - useUpdateInvoice - formData:', formData)
      console.log('🔍 DEBUG - useUpdateInvoice - Calling invoicesAPI.update')
      return invoicesAPI.update(id, formData)
    },
    {
      onSuccess: (data, variables) => {
        console.log('🔍 DEBUG - useUpdateInvoice - onSuccess - data:', data)
        console.log('🔍 DEBUG - useUpdateInvoice - onSuccess - variables:', variables)
        
        // Invalidate all related queries
        queryClient.invalidateQueries(['invoices'])
        queryClient.invalidateQueries(['invoice', variables.id])
        queryClient.invalidateQueries(['dashboard'])
        queryClient.invalidateQueries(['clients'])
        
        // Force immediate refetch of dashboard
        queryClient.refetchQueries(['dashboard'], { active: true })
        
        enqueueSnackbar('Factura actualizada exitosamente', { variant: 'success' })
      },
      onError: (error) => {
        console.error('❌ DEBUG - useUpdateInvoice - onError:', error)
        const message = formatApiError(error)
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

export const useUpdateInvoiceStatus = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    ({ id, status }) => invoicesAPI.updateStatus(id, status),
    {
      onSuccess: () => {
        // Invalidate all related queries
        queryClient.invalidateQueries(['invoices'])
        queryClient.invalidateQueries(['dashboard'])
        queryClient.invalidateQueries(['clients'])
        
        // Force immediate refetch of dashboard
        queryClient.refetchQueries(['dashboard'], { active: true })
        
        enqueueSnackbar('Estado de factura actualizado', { variant: 'success' })
      },
      onError: (error) => {
        const message = formatApiError(error)
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

export const useDeleteInvoice = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    (id) => invoicesAPI.delete(id),
    {
      onSuccess: () => {
        // Invalidate all related queries
        queryClient.invalidateQueries(['invoices'])
        queryClient.invalidateQueries(['dashboard'])
        queryClient.invalidateQueries(['clients'])
        queryClient.invalidateQueries(['reports'])
        
        // Force immediate refetch of dashboard
        queryClient.refetchQueries(['dashboard'], { active: true })
        
        enqueueSnackbar('Factura eliminada exitosamente', { variant: 'success' })
      },
      onError: (error) => {
        const message = formatApiError(error)
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

export const useInvoicePdf = (id) => {
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    () => invoicesAPI.getPdfUrl(id),
    {
      onError: (error) => {
        const message = formatApiError(error)
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}
